/* eslint @typescript-eslint/no-non-null-assertion: 0 */
import { AnimatePresence, Point } from 'framer-motion';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import VideoIndicator from 'components/VideoIndicator/VideoIndicator';
import { MediaCMS } from 'utils/sharedStrapiQueries/sharedTypes';

import { ImageSliderVariants } from './ImageSlider.animations';
import { wrap } from './ImageSlider.utils';

import * as S from './ImageSlider.styles';

interface handleDragEndProps {
  offset: Point;
  velocity: Point;
}

export interface ImageSliderProps {
  media: Array<MediaCMS>;
  autoplay?: boolean;
  manualPlay?: boolean;
  autoplaySpeed?: number;
}

const ImageSlider: React.FunctionComponent<ImageSliderProps> = ({
  media,
  autoplay = true,
  manualPlay = true,
  autoplaySpeed = 5000,
  ...rest
}) => {
  const autoplayIntervalRef = useRef<NodeJS.Timeout>();

  const [timer, setTimer] = useState<number>(0);
  const [innerHeight, setInnerHeight] = useState<number>(0);
  const [isSlideToRight, setIsSlideToRight] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState(autoplay);
  const [[page, direction], setPage] = useState<Array<number>>([0, 0]);

  const { ref, inView } = useInView();

  const imageIndex = wrap(0, media.length, page);

  const slideRightCondition =
    imageIndex === media.length - 1 ? 0 : imageIndex + 1;
  const slideLeftCondition =
    imageIndex === 0 ? media.length - 1 : imageIndex - 1;

  const paginate = (newDirection: number) => {
    setPage(prev => [prev[0] + newDirection, newDirection]);
  };

  const handleDrag = useCallback(
    (
      e: MouseEvent | TouchEvent | PointerEvent,
      { offset }: handleDragEndProps
    ) => setIsSlideToRight(offset.x < 0),
    []
  );

  const handleDragEnd = (
    e: MouseEvent | TouchEvent | PointerEvent,
    { offset }: handleDragEndProps
  ) => {
    if (offset.y > -100 && offset.y < 100) {
      offset.x < 0 ? paginate(1) : paginate(-1);
      setIsSlideToRight(offset.x >= 0);
    } else {
      paginate(0);
    }
  };

  const handleAutoPlay = () => {
    paginate(1);
  };

  useEffect(() => {
    setInnerHeight(window.innerHeight);

    isPlaying && inView
      ? (autoplayIntervalRef.current = setInterval(
          handleAutoPlay,
          autoplaySpeed
        ))
      : clearInterval(autoplayIntervalRef.current!);

    return () => clearInterval(autoplayIntervalRef.current!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, autoplaySpeed, inView]);

  useEffect(() => {
    if (isPlaying && inView && timer < autoplaySpeed / 1000)
      setTimeout(() => setTimer(timer + 1), 1000);
    else setTimer(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, timer]);

  return (
    <S.ImageSliderWrapper
      ref={ref}
      $nextImage={
        media[isSlideToRight ? slideRightCondition : slideLeftCondition]?.url
      }
      $innerHeight={innerHeight}
      {...rest}
    >
      <S.DragContainer>
        <AnimatePresence initial={false} custom={direction}>
          <S.DragWrapper
            key={page}
            custom={direction}
            initial="enter"
            animate="center"
            variants={ImageSliderVariants}
            exit="exit"
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            onDrag={handleDrag}
            onDragEnd={handleDragEnd}
          >
            <S.Image
              src={media[imageIndex]?.url || ''}
              alt={media[imageIndex]?.alternativeText || ''}
            />
          </S.DragWrapper>
        </AnimatePresence>
      </S.DragContainer>

      <S.Container>
        {manualPlay && (
          <S.IndicatorWrapper>
            <VideoIndicator
              timer={autoplaySpeed}
              isPause={!isPlaying}
              onPlay={() => setTimeout(() => setIsPlaying(true), 500)}
              onPause={() => setTimeout(() => setIsPlaying(false), 1000)}
              size={0.35}
            />
          </S.IndicatorWrapper>
        )}
      </S.Container>

      <S.Container>
        <S.DotsContainer>
          {media.map((image, index) => (
            <S.Dot
              key={`${image.alternativeText}-${index}`}
              isActive={imageIndex === index}
            />
          ))}
        </S.DotsContainer>
      </S.Container>
    </S.ImageSliderWrapper>
  );
};

export default ImageSlider;
